import { Box } from '@mui/material';
import {
    DataGridPro,
    GridColDef,
    GridEventListener,
    GridPinnedRowsProp,
    GridRowIdGetter,
    GridRowProps,
} from '@mui/x-data-grid-pro';

import { DEFAULT_BORDER } from 'src/constants';
import {
    disableCopyEventHandler,
    disableCutEventHandler,
} from 'src/hooks/useEventListener/eventHandlers';

import CustomPagination from './components/CustomPagination';
import CustomToolbar, { AddRecordButtonConfig } from './components/CustomToolbar';

export type NPIDataGridRow = GridRowProps;
export type NPIDataGridColDef = GridColDef;
export type NPIDataGridGetRowIdGetter = GridRowIdGetter;

type NPIDataGridProps = {
    rows: NPIDataGridRow[];
    columns: NPIDataGridColDef[];
    getRowId: NPIDataGridGetRowIdGetter;
    dataTestId: string;
    title?: string;
    pagination?: {
        isEnabled?: boolean;
        pageSizeOptions?: number[];
    };
    filtering?: {
        isEnabled: boolean;
    };
    rowReordering?: {
        isEnabled: boolean;
    };
    addRecordButtonConfig?: AddRecordButtonConfig;
    pinnedRow?: GridPinnedRowsProp<any>;
    rowOrderChange?: GridEventListener<'rowOrderChange'>;
    maxHeight?: string;
    disableCopyCut?: boolean;
};

export function NPIDataGrid({
    rows,
    columns,
    getRowId,
    title,
    pagination = {
        isEnabled: true,
        pageSizeOptions: [5, 10, 25],
    },
    filtering = {
        isEnabled: true,
    },
    dataTestId = `${title ? `${title}-` : ''}data-grid`,
    rowReordering = { isEnabled: false },
    addRecordButtonConfig,
    pinnedRow,
    rowOrderChange,
    maxHeight,
    disableCopyCut = true,
}: NPIDataGridProps) {
    return (
        <Box
            sx={{
                ...DEFAULT_BORDER,
                width: '100%',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight,
                }}
                onCopy={disableCopyCut ? disableCopyEventHandler : null}
                onCut={disableCopyCut ? disableCutEventHandler : null}
            >
                <DataGridPro
                    data-testid={dataTestId}
                    sx={{
                        '.MuiDataGrid-footerContainer': {
                            height: '100%',
                        },
                        '.MuiDataGrid-toolbarContainer': {
                            height: '100%',
                        },
                        '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            padding: '8px',
                            backgroundColor: 'transparent !important',
                            border: 'none',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'transparent !important',
                        },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                            {
                                outline: 'none',
                            },
                    }}
                    initialState={{
                        pagination: pagination?.isEnabled
                            ? { paginationModel: { pageSize: 5 } }
                            : null,
                    }}
                    pagination={pagination?.isEnabled}
                    rows={rows}
                    columns={columns}
                    getRowId={getRowId}
                    disableColumnFilter={!filtering?.isEnabled}
                    getRowHeight={() => {
                        return 'auto';
                    }}
                    disableDensitySelector
                    disableColumnPinning
                    disableColumnSelector
                    disableRowSelectionOnClick
                    slots={{
                        toolbar: () => {
                            return (
                                <CustomToolbar
                                    title={title}
                                    addRecordButtonConfig={addRecordButtonConfig}
                                />
                            );
                        },
                        pagination: pagination?.isEnabled ? CustomPagination : null,
                        noRowsOverlay: () => {
                            return (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        padding: '20px',
                                    }}
                                >
                                    No Records Found.
                                </Box>
                            );
                        },
                    }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    pageSizeOptions={pagination?.pageSizeOptions}
                    rowReordering={rowReordering?.isEnabled}
                    pinnedColumns={{ right: ['actions'] }}
                    pinnedRows={pinnedRow}
                    onRowOrderChange={rowOrderChange}
                />
            </div>
        </Box>
    );
}
